/**
 * <cart-gift-progress> — pasek postępu + oferta batonu za 1 zł.
 *
 * Pokazuje ile brakuje do progu (300 zł). Po przekroczeniu odsłania OFERTĘ: 2 batony
 * (GYM / GLAMOUR) po 1 zł z przyciskiem „Dodaj". Klient wybiera JEDEN (max 1 łącznie) —
 * dodanie jednego wymienia drugi. Baton oznaczany property `_gift` (nie liczy się do progu).
 * „1 zł" zapewniają automatyczne rabaty w Shopify (−5,99 / −4,99, min. 300 zł).
 *
 * NIE auto-dodaje (płatna pozycja — wymagana świadoma akcja klienta) i NIE auto-usuwa
 * przy spadku poniżej progu (decyzja Daniela).
 *
 * Atrybuty:
 *   data-threshold     próg w groszach (30000 = 300 zł)
 *   data-currency      waluta (PLN) — na innych rynkach ukryte
 *   data-bar-variants  CSV numerycznych ID wariantów batonów (gratisowalnych)
 */
class CartGiftProgress extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.dataset.threshold || '30000', 10);
    this.currency = this.dataset.currency || 'PLN';
    this.barVariants = String(this.dataset.barVariants || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    this.busy = false;
    this._t = [];
  }

  connectedCallback() {
    this.text = this.querySelector('[data-gift-text]');
    this.bar = this.querySelector('[data-gift-bar]');
    this.offer = this.querySelector('[data-gift-offer]');
    this.bindButtons();
    this.render();

    this._onChange = () => {
      // Re-render na KILKU opóźnieniach — przy 1. dodaniu do PUSTEGO koszyka morph motywu
      // dochodzi późno i nadpisuje nasz stan (przyciski/tekst/recs); późne renderowania to łapią.
      [90, 600, 1300].forEach((d, i) => {
        clearTimeout(this._t[i]);
        this._t[i] = setTimeout(() => this.render(), d);
      });
    };
    document.addEventListener('cart:update', this._onChange);
    document.addEventListener('discount:update', this._onChange);

    this._drawerEl = this.closest('cart-drawer-component') || this.closest('dialog');
    if (this._drawerEl) this._drawerEl.addEventListener('dialog:open', this._onChange);
    // KLUCZOWE: po morphie motyw wstawia NOWY element — connectedCallback odpalany jest tutaj,
    // a stare timery zostały na odpiętym elemencie. Odpalamy multi-delay re-render na nowym.
    this._onChange();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this._onChange);
    document.removeEventListener('discount:update', this._onChange);
    if (this._drawerEl && this._onChange) this._drawerEl.removeEventListener('dialog:open', this._onChange);
    (this._t || []).forEach((t) => clearTimeout(t));
  }

  bindButtons() {
    // Delegacja — JEDEN listener na elemencie, przeżywa morph DOM koszyka (morph podmienia
    // przyciski; listener na samych przyciskach by zginął). Guard przed podwójnym bindem.
    if (this._bound) return;
    this._bound = true;
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gift-add]');
      if (btn && this.contains(btn) && !btn.disabled) this.onAddClick(btn.dataset.variantId);
    });
  }

  async render() {
    if (this.busy) return;
    let cart;
    try {
      cart = await fetch('/cart.js').then((r) => r.json());
    } catch (e) {
      this.hidden = true;
      return;
    }

    const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || cart.currency;
    if (cur !== this.currency || !cart.items || cart.items.length === 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    // WYMIANA: tylko JEDEN baton naraz (checkout i tak daje rabat na jeden — wymiennie, domyślnie droższy).
    const giftLine = cart.items.find(
      (i) => this.barVariants.includes(String(i.variant_id)) && i.properties && i.properties._gift
    );
    const addedId = giftLine ? String(giftLine.variant_id) : null;
    const qualifying = cart.items
      .filter((i) => !(i.properties && i.properties._gift))
      .reduce((s, i) => s + i.final_line_price, 0);

    const remaining = Math.max(0, this.threshold - qualifying);
    const pct = Math.max(2, Math.min(100, Math.round((qualifying / this.threshold) * 100)));
    if (this.bar) this.bar.style.width = pct + '%';

    const unlocked = remaining <= 0;
    if (this.text) {
      if (unlocked) {
        this.text.innerHTML = '🎉 <strong>Odbierz baton za 1 zł</strong> — wybierz jeden:';
        this.classList.add('is-complete');
      } else {
        this.text.innerHTML = `Do <strong>batonu za 1 zł</strong> brakuje <strong>${this.formatMoney(remaining)}</strong>`;
        this.classList.remove('is-complete');
      }
    }

    // Karty batonów widoczne ZAWSZE (też przed progiem — ze zdjęciami). Gdy zablokowane
    // (poniżej progu) przyciski „Dodaj" nieaktywne; pasek pokazuje ile brakuje.
    this.classList.toggle('is-locked', !unlocked);
    if (this.offer) this.offer.hidden = false;

    this.querySelectorAll('[data-bar-card]').forEach((card) => {
      const vid = card.dataset.barCard;
      const btn = card.querySelector('[data-gift-add]');
      const isAdded = vid === addedId;
      card.classList.toggle('is-added', isAdded);
      if (!btn) return;
      if (isAdded) {
        btn.textContent = '✓ W koszyku';
        btn.disabled = false;
        btn.classList.add('is-added');
        btn.classList.remove('is-locked');
      } else {
        btn.textContent = 'Dodaj';
        btn.classList.remove('is-added');
        btn.disabled = !unlocked;
        btn.classList.toggle('is-locked', !unlocked);
      }
    });
  }

  async onAddClick(variantId) {
    if (this.busy || !variantId) return;
    this.busy = true;
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      // WYMIANA: dowolny baton-gift już w koszyku (max 1 naraz)
      const giftLine = cart.items.find(
        (i) => this.barVariants.includes(String(i.variant_id)) && i.properties && i.properties._gift
      );

      let last;
      if (giftLine && String(giftLine.variant_id) === String(variantId)) {
        // ten sam → odznacz (usuń)
        last = await this.change(giftLine.key, 0, true);
      } else {
        // inny/żaden → WYMIANA: usuń poprzedni baton, dodaj wybrany (tylko jeden naraz)
        if (giftLine) await this.change(giftLine.key, 0, false);
        last = await this.add(variantId, true);
      }
      if (last && last.sections) this.applyCartSections(last.sections);
    } catch (e) {
      /* ignore */
    } finally {
      this.busy = false;
    }
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
  }

  sectionsParam() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((c) => ids.add(c.dataset.sectionId));
    return ids.size ? Array.from(ids).join(',') : null;
  }

  async add(variantId, withSections) {
    const body = { items: [{ id: Number(variantId), quantity: 1, properties: { _gift: '1' } }] };
    if (withSections) {
      const s = this.sectionsParam();
      if (s) body.sections = s;
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  async change(key, quantity, withSections) {
    const body = { id: key, quantity };
    if (withSections) {
      const s = this.sectionsParam();
      if (s) body.sections = s;
    }
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  applyCartSections(sections) {
    if (!sections) return;
    for (const html of Object.values(sections)) {
      if (!html) continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshInner = doc.querySelector('.cart-drawer__inner');
      const liveInner = document.querySelector('.cart-drawer__inner');
      if (freshInner && liveInner) {
        liveInner.innerHTML = freshInner.innerHTML;
        continue;
      }
      const freshPage = doc.querySelector('.cart-page');
      const livePage = document.querySelector('.cart-page');
      if (freshPage && livePage) livePage.innerHTML = freshPage.innerHTML;
    }
  }

  formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    const amount = (cents / 100).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${amount} ${currency}`.trim();
  }
}

if (!customElements.get('cart-gift-progress')) {
  customElements.define('cart-gift-progress', CartGiftProgress);
}

/**
 * Globalny obserwator drawera koszyka. Przy 1. dodaniu do PUSTEGO koszyka Horizon podmienia DOM
 * przez startViewTransition + replaceChildren (empty-cart-template) — INNĄ ścieżką niż morph/eventy,
 * przez co cart-gift-progress i cart-upsell renderują się w niepełnym stanie (przyciski niezablokowane,
 * brak recs, brak tekstu progu). Ten obserwator re-renderuje OBA komponenty po każdej zmianie DOM
 * drawera, niezależnie od ścieżki renderowania. Pauza na czas własnego renderu = brak pętli.
 */
(function () {
  if (window.__ggCartObs) return;
  window.__ggCartObs = true;

  let debT, reT;
  const obs = new MutationObserver(() => {
    clearTimeout(debT);
    debT = setTimeout(rerender, 150); // czekaj aż morph/view-transition się ustabilizuje
  });

  function rerender() {
    clearTimeout(debT);
    obs.disconnect(); // pauza — własne zmiany komponentów nie wywołają pętli
    document.querySelectorAll('cart-gift-progress, cart-upsell').forEach((el) => {
      if (typeof el.render === 'function') {
        if ('loadedFor' in el) el.loadedFor = null;
        try {
          el.render();
        } catch (e) {
          /* ignore */
        }
      }
    });
    clearTimeout(reT);
    reT = setTimeout(connect, 1000); // reconnect po async renderze (fetch recs)
  }

  function connect() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer) obs.observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    if (!document.querySelector('cart-drawer-component')) {
      setTimeout(init, 300);
      return;
    }
    connect();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
